import { computed, reactive, ref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  fetchDowntimeByDay,
  fetchDowntimeByLogist,
  fetchDowntimeByReason,
  fetchDowntimeByWarehouse,
  fetchDowntimeKpis,
  fetchDowntimeTable,
} from '@/backend/statisticsApi';
import { toIsoDate } from './format';

export const DOWNTIMES_PAGE_SIZE = 50;
const STORAGE_KEY = 'statisticsDowntimesFilters';

// Сентинел «ничего не выбрано»: в API пустой classifications = «все»,
// поэтому пустая выборка кодируется несуществующим значением (IN-матч 0 строк).
const NONE_SELECTION = ['__none__'];

function defaultPeriod() {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
}

function loadStoredFilters() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

const LIST_KEYS = ['responsibles', 'lifecycles', 'operationTypes'];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function useDowntimesStatistics() {
  const queryClient = useQueryClient();
  const stored = loadStoredFilters();
  const defaults = defaultPeriod();
  const filters = reactive({
    dateFrom:
      typeof stored?.dateFrom === 'string' && DATE_RE.test(stored.dateFrom)
        ? stored.dateFrom
        : defaults.dateFrom,
    dateTo:
      typeof stored?.dateTo === 'string' && DATE_RE.test(stored.dateTo)
        ? stored.dateTo
        : defaults.dateTo,
    responsibles: [],
    lifecycles: [],
    operationTypes: [],
  });
  for (const key of LIST_KEYS) {
    if (Array.isArray(stored?.[key])) {
      filters[key] = stored[key].filter((v) => typeof v === 'string');
    }
  }

  // Единственный источник истины по классификациям:
  // null = все; массив = явная выборка (может быть пустой = «ничего»).
  // Дропдаун и легенда пишут только сюда; API-параметр и legend.selected — производные.
  const storedClassifications = Array.isArray(stored?.classifications)
    ? stored.classifications.filter((v) => typeof v === 'string')
    : null;
  const statusSelection = ref(
    storedClassifications && storedClassifications.length ? storedClassifications : null
  );

  watch(
    [filters, statusSelection],
    () => {
      try {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ ...filters, classifications: statusSelection.value || [] })
        );
      } catch (e) {
        /* ignore */
      }
    },
    { deep: true }
  );

  const tableSorting = ref([{ id: 'planned_date', desc: true }]);
  const tablePage = ref(1);
  // Выбранный день (клик по столбцу) — точечный фильтр таблицы.
  const selectedDay = ref(null);
  // Топ N складов для графика.
  const warehouseLimit = ref(15);

  const filtersKey = computed(() => JSON.stringify(filters));
  const statusKey = computed(() =>
    statusSelection.value === null ? 'all' : JSON.stringify(statusSelection.value)
  );

  // Параметры, зависящие от выборки статусов (всё, кроме by-day).
  function statusFilters() {
    const selection = statusSelection.value;
    return {
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      responsibles: filters.responsibles,
      classifications:
        selection === null ? [] : selection.length ? selection : NONE_SELECTION,
      lifecycles: filters.lifecycles,
      operationTypes: filters.operationTypes,
    };
  }

  const kpisQuery = useQuery({
    queryKey: computed(() => ['statistics-downtimes', 'kpis', filtersKey.value, statusKey.value]),
    queryFn: () => fetchDowntimeKpis(statusFilters()),
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  // График по дням всегда получает все классификации:
  // видимость статусов управляется легендой (shared state с фильтром).
  // Ключ — только реальные параметры запроса (без выборки статусов).
  const byDayQuery = useQuery({
    queryKey: computed(() => ['statistics-downtimes', 'by-day', filtersKey.value]),
    queryFn: () =>
      fetchDowntimeByDay({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        responsibles: filters.responsibles,
        classifications: [],
        lifecycles: filters.lifecycles,
        operationTypes: filters.operationTypes,
      }),
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const byLogistQuery = useQuery({
    queryKey: computed(() => ['statistics-downtimes', 'by-logist', filtersKey.value, statusKey.value]),
    queryFn: () => fetchDowntimeByLogist(statusFilters()),
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const byWarehouseQuery = useQuery({
    queryKey: computed(() => [
      'statistics-downtimes',
      'by-warehouse',
      filtersKey.value,
      statusKey.value,
      warehouseLimit.value,
    ]),
    queryFn: () => fetchDowntimeByWarehouse(statusFilters(), warehouseLimit.value),
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const byReasonQuery = useQuery({
    queryKey: computed(() => ['statistics-downtimes', 'by-reason', filtersKey.value, statusKey.value]),
    queryFn: () => fetchDowntimeByReason(statusFilters()),
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  // Список логистов для фильтра — без фильтров, чтобы список не сужался при выборе.
  const logistOptionsQuery = useQuery({
    queryKey: ['statistics-downtimes', 'logist-options'],
    queryFn: () => fetchDowntimeByLogist(null),
    staleTime: 10 * 60 * 1000,
  });

  const tableQuery = useQuery({
    queryKey: computed(() => [
      'statistics-downtimes',
      'table',
      filtersKey.value,
      statusKey.value,
      selectedDay.value,
      tableSorting.value,
      tablePage.value,
    ]),
    queryFn: () => {
      const sort = tableSorting.value[0] || { id: 'planned_date', desc: true };
      return fetchDowntimeTable({
        filters: effectiveFilters(),
        sortBy: sort.id,
        asc: !sort.desc,
        limit: DOWNTIMES_PAGE_SIZE,
        offset: (tablePage.value - 1) * DOWNTIMES_PAGE_SIZE,
      });
    },
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  function effectiveFilters() {
    const base = statusFilters();
    if (!selectedDay.value) return base;
    return { ...base, dateFrom: selectedDay.value, dateTo: selectedDay.value };
  }

  // Применение сохранённого пресета фильтров (валидация как при restore).
  function applySnapshot(snap) {
    if (!snap || typeof snap !== 'object') return;
    if (typeof snap.dateFrom === 'string' && DATE_RE.test(snap.dateFrom)) {
      filters.dateFrom = snap.dateFrom;
    }
    if (typeof snap.dateTo === 'string' && DATE_RE.test(snap.dateTo)) {
      filters.dateTo = snap.dateTo;
    }
    for (const key of LIST_KEYS) {
      if (Array.isArray(snap[key])) {
        filters[key] = snap[key].filter((v) => typeof v === 'string');
      }
    }
    const cls = Array.isArray(snap.classifications)
      ? snap.classifications.filter((v) => typeof v === 'string')
      : null;
    statusSelection.value = cls && cls.length ? cls : null;
    selectedDay.value = null;
  }

  const refreshAll = async () => {
    await queryClient.invalidateQueries({ queryKey: ['statistics-downtimes'] });
  };

  return {
    filters,
    filtersKey,
    statusSelection,
    selectedDay,
    warehouseLimit,
    applySnapshot,
    kpisQuery,
    byDayQuery,
    byLogistQuery,
    byWarehouseQuery,
    byReasonQuery,
    logistOptionsQuery,
    tableQuery,
    tableSorting,
    tablePage,
    refreshAll,
  };
}
